import logger from "bragi-browser";
import logToDatadog from "./DataDogLogger";

const TALENTBAIT_VERSION = "2.35";

window.TALENTBAIT_VERSION = TALENTBAIT_VERSION;
window.loggerShared = logger;

class FrontendLogger {
  constructor(bragiLogger) {
    /**
     * The logger instance of Bragi Browser. This shouldn't be used directly.
     * @deprecated
     */
    this.logger = bragiLogger;
  }

  /**
   * Logs a message with the `debug` log level. This is only enabled in localhost.
   *
   * @param {string} namespace - The namespace of the log message.
   * @param {string} message - The message to log.
   * @param {Record<string, unknown> | undefined} arg - The argument to log.
   * @returns {void}
   */
  debug(namespace, message, arg) {
    if (window.location.href.includes("localhost")) {
      logger.log(namespace, message, arg);
    }
  }

  /**
   * Logs a message. In development and testing environments, this will be logged to the Browser console.
   * In production and staging, this will be logged to Datadog.
   *
   * @param {string} namespace - The namespace of the log message.
   * @param {string} message - The message to log.
   * @param {Record<string, unknown> | undefined} arg - The argument to log.
   * @returns {void}
   */
  log(namespace, message, arg) {
    if (this.#shouldLogToBrowser()) {
      logger.log(namespace, message, arg);
    } else {
      logToDatadog(namespace, message, arg);
    }
  }

  /**
   * Logs a message with the `warn` log level.
   *
   * @param {string} namespace - The namespace of the log message.
   * @param {string} message - The message to log.
   * @param {Record<string, unknown> | undefined} arg - The argument to log.
   */
  warn(namespace, message, arg) {
    // Bragi uses `warn` as a log level, but Datadog uses `warning`.
    const isDevelopment = this.#shouldLogToBrowser();
    const namespaceWithLogLevel = isDevelopment ? `warn:${namespace}` : `warning:${namespace}`;

    this.log(namespaceWithLogLevel, message, arg);
  }

  /**
   * Logs a message with the `info` log level.
   *
   * @param {string} namespace - The namespace of the log message.
   * @param {string} message - The message to log.
   * @param {Record<string, unknown> | undefined} arg - The argument to log.
   */
  info(namespace, message, arg) {
    const namespaceWithLogLevel = `info:${namespace}`;
    this.log(namespaceWithLogLevel, message, arg);
  }

  /**
   * Logs a message with the `error` log level.
   *
   * @param {string} namespace - The namespace of the log message.
   * @param {string} message - The message to log.
   * @param {Record<string, unknown> | undefined} arg - The argument to log.
   */
  error(namespace, message, arg) {
    const namespaceWithLogLevel = `error:${namespace}`;
    this.log(namespaceWithLogLevel, message, arg);
  }

  /**
   * Logs a message with the `alert` log level.
   *
   * @param {string} namespace - The namespace of the log message.
   * @param {string} message - The message to log.
   * @param {Record<string, unknown> | undefined} arg - The argument to log.
   */
  alert(namespace, message, arg) {
    // Datadog uses `alert` as a log level but Bragi doesn't support it.
    const isDevelopment = this.#shouldLogToBrowser();
    const namespaceWithLogLevel = isDevelopment ? `error:${namespace}` : `alert:${namespace}`;
    this.log(namespaceWithLogLevel, message, arg);
  }

  /**
   * Checks if the log should be logged to the Browser console.
   *
   * @returns {boolean}
   * @private
   */
  #shouldLogToBrowser() {
    const isLocalHost = window.location.href.includes("localhost");
    const isDevelopment = window.env === "development" || window.env === "testing";

    return isLocalHost || isDevelopment;
  }
}

const frontendLogger = new FrontendLogger(logger);

export default frontendLogger;
